import { InlineLoading, InlineNotification } from '@carbon/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';
import { Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { TitleWorkspaceHeader } from '../common/title-workspace-header';
import { TitleWorkspaceDocumentIndexPage } from '../document-index/page';
import { TitleWorkspaceOverviewPage } from '../overview';
import { TitleWorkspaceRunsheetIndexPage } from '../runsheet/page';
import { TitleWorkspaceWellsIndexPage } from '../well-index/page';
import { StartingInterestPage } from '../starting-interest/page';
import { Route } from 'components/route';
import { createResourceAtom } from 'lib/atoms';
import { TitleWorkspaceResponse } from 'types/api-responses';
import { resetCalculationCacheAtom } from '../overview/atoms';
import { RunsheetTimelinePage } from '../runsheet-timeline/page';
import { TitleWorkspaceEntitiesPage } from '../entities/page';
import AddDocumentModal from 'components/add-document-modal/add-document-modal';
import { FeatureFlagWindow } from 'types/window';
import { TitleWorkspaceAgent } from 'components/title-workspace-agent/title-workspace-agent';
import { useAgentDockWidth } from 'components/agent-chat';
import { TITLE_WORKSPACE_AGENT } from 'components/agent-chat/agents/title-workspace-agent';
import { agentOpenAtom } from '../atoms';
import { titleCalculationActivityAtom } from 'atoms/title-calculation-atoms';
import style from './page.module.scss';

declare let window: FeatureFlagWindow;

const TitleWorkspaceEntrypointPage = () => {
  const showAddDocument = window.CONFIG?.featureFlags.autoprovisionerV2;
  const location = useLocation();
  const [resource, id] = location.pathname.split('/').filter((el) => el);
  const titleWorkspaceHref = `/${resource}/${id}`;
  const { path } = useRouteMatch();
  const titleWorkspaceAtom = useMemo(
    () => createResourceAtom<TitleWorkspaceResponse>(titleWorkspaceHref),
    [titleWorkspaceHref]
  );
  const titleWorkspace = useAtomValue(titleWorkspaceAtom);
  const refetchTitleWorkspace = useSetAtom(titleWorkspaceAtom);
  const resetCaches = useSetAtom(resetCalculationCacheAtom);
  const setAgentOpen = useSetAtom(agentOpenAtom);
  // 0 while the dock is closed, so the padding below is a no-op then.
  const agentWidth = useAgentDockWidth(TITLE_WORKSPACE_AGENT);
  const calculationActivity = useAtomValue(titleCalculationActivityAtom);
  const lastSeenCompletedAtRef = useRef<string | null>(null);

  useEffect(() => {
    return () => resetCaches();
  }, [resetCaches]);

  useEffect(() => {
    setAgentOpen(false);
  }, [titleWorkspaceHref, setAgentOpen]);

  useEffect(() => {
    const completedAt = calculationActivity.completedAt;
    if (!completedAt) return;
    if (lastSeenCompletedAtRef.current === completedAt) return;
    lastSeenCompletedAtRef.current = completedAt;
    refetchTitleWorkspace();
  }, [calculationActivity.completedAt, refetchTitleWorkspace]);

  if (titleWorkspace.data && 'error' in titleWorkspace.data)
    return (
      <InlineNotification
        kind="error"
        title={titleWorkspace.data.error}
        lowContrast
      />
    );
  if (titleWorkspace.loading || !titleWorkspace.data) return <InlineLoading />;

  const workspaceData = titleWorkspace.data;
  const agentResource = workspaceData.resources.agentMessages;
  const agentHref = agentResource?.href;
  const agentModel = agentResource?.model ?? null;
  return (
    <>
      <div
        className={style.contentWrapper}
        style={agentWidth ? { paddingRight: agentWidth } : undefined}
      >
        <Switch>
          <Route exact path={`${path}/`}>
            <TitleWorkspaceHeader
              key={location.pathname}
              subtitle="Workspace"
              titleWorkspace={titleWorkspace.data}
            >
              <TitleWorkspaceOverviewPage
                titleWorkspaceHref={titleWorkspaceHref}
              />
            </TitleWorkspaceHeader>
          </Route>
          <Route exact path={`${path}/documents`}>
            <TitleWorkspaceHeader
              key={location.pathname}
              subtitle="Document Index"
              actions={showAddDocument && <AddDocumentModal />}
              titleWorkspace={titleWorkspace.data}
            >
              <TitleWorkspaceDocumentIndexPage
                titleWorkspace={titleWorkspace.data}
              />
            </TitleWorkspaceHeader>
          </Route>
          <Route exact path={`${path}/wells`}>
            <TitleWorkspaceHeader
              key={location.pathname}
              subtitle="Wells Index"
              titleWorkspace={titleWorkspace.data}
            >
              <TitleWorkspaceWellsIndexPage
                titleWorkspace={titleWorkspace.data}
              />
            </TitleWorkspaceHeader>
          </Route>
          <Route exact path={`${path}/entities`}>
            <TitleWorkspaceHeader
              key={location.pathname}
              subtitle="Entities"
              titleWorkspace={titleWorkspace.data}
            >
              <TitleWorkspaceEntitiesPage
                titleWorkspace={titleWorkspace.data}
              />
            </TitleWorkspaceHeader>
          </Route>
          <Route exact path={`${path}/runsheet`}>
            <TitleWorkspaceHeader
              key={location.pathname}
              subtitle="Runsheet"
              titleWorkspace={titleWorkspace.data}
            >
              <TitleWorkspaceRunsheetIndexPage
                titleWorkspaceHref={titleWorkspaceHref}
              />
            </TitleWorkspaceHeader>
          </Route>
          <Route exact path={`${path}/starting_interest`}>
            <TitleWorkspaceHeader
              key={location.pathname}
              title="Workspace Settings"
              subtitle="Workspace Settings"
              titleWorkspace={titleWorkspace.data}
              hideTabs
            >
              <StartingInterestPage workspaceHref={titleWorkspace.data.href} />
            </TitleWorkspaceHeader>
          </Route>
          <Route exact path={`${path}/timeline`}>
            <RunsheetTimelinePage titleWorkspace={titleWorkspace.data} />
          </Route>
        </Switch>
      </div>
      {agentHref && (
        <TitleWorkspaceAgent
          agentMessagesHref={agentHref}
          workspaceId={workspaceData.id}
          workspaceName={workspaceData.name}
          lastCalculated={workspaceData.lastCalculated}
          model={agentModel}
          calculationActivity={calculationActivity}
        />
      )}
    </>
  );
};

export { TitleWorkspaceEntrypointPage };
